import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import type { UserProfile, Client } from '../page';

interface ProjectMembership {
  project_id: string;
  role: string;
  _project_name?: string;
  _client_name?: string;
  _project_status?: string;
}

interface UserDetailDrawerProps {
  user: UserProfile;
  clients: Client[];
  onClose: () => void;
  onEdit: () => void;
  onToggleActive: () => void;
  onRefresh: () => void;
}

const ROLE_CONFIG: Record<string, { label: string; color: string }> = {
  admin: { label: 'Admin', color: 'bg-cyan-100 text-cyan-700' },
  client: { label: 'Cliente', color: 'bg-violet-100 text-violet-700' },
  member: { label: 'Membro', color: 'bg-slate-100 text-slate-600' },
};

const PROJECT_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  proposta: { label: 'Proposta', color: 'bg-slate-100 text-slate-600' },
  ativo: { label: 'Ativo', color: 'bg-emerald-100 text-emerald-700' },
  pausado: { label: 'Pausado', color: 'bg-amber-100 text-amber-700' },
  concluido: { label: 'Concluído', color: 'bg-cyan-100 text-cyan-700' },
  cancelado: { label: 'Cancelado', color: 'bg-red-100 text-red-600' },
};

export default function UserDetailDrawer({ user, clients, onClose, onEdit, onToggleActive, onRefresh }: UserDetailDrawerProps) {
  const [memberships, setMemberships] = useState<ProjectMembership[]>([]);
  const [allProjects, setAllProjects] = useState<{ id: string; name: string; status: string; client_id: string | null }[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingProject, setAddingProject] = useState(false);
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedRole, setSelectedRole] = useState('viewer');

  const loadMemberships = useCallback(async () => {
    setLoading(true);

    const [membRes, projRes] = await Promise.all([
      supabase.from('project_members').select('project_id, role').eq('user_id', user.id),
      supabase.from('projects').select('id, name, status, client_id').order('name'),
    ]);

    const rawMembers = membRes.data || [];
    const rawProjects = projRes.data || [];

    const clientIds = [...new Set(rawProjects.map(p => p.client_id).filter(Boolean))];
    const { data: clientsData } = clientIds.length > 0
      ? await supabase.from('clients').select('id, name').in('id', clientIds)
      : { data: [] };
    const cMap = Object.fromEntries((clientsData || []).map((c: { id: string; name: string }) => [c.id, c.name]));

    const projMap = Object.fromEntries(rawProjects.map(p => [p.id, p]));

    const enriched: ProjectMembership[] = rawMembers.map(m => {
      const proj = projMap[m.project_id];
      return {
        project_id: m.project_id,
        role: m.role,
        _project_name: proj?.name || '—',
        _client_name: proj?.client_id ? cMap[proj.client_id] || '—' : '—',
        _project_status: proj?.status || '',
      };
    });

    setMemberships(enriched);
    setAllProjects(rawProjects);
    setLoading(false);
  }, [user.id]);

  useEffect(() => { loadMemberships(); }, [loadMemberships]);

  const availableProjects = allProjects.filter(
    p => !memberships.some(m => m.project_id === p.id)
  );

  const handleAddProject = async () => {
    if (!selectedProject) return;
    const { error } = await supabase.from('project_members').insert({
      project_id: selectedProject,
      user_id: user.id,
      role: selectedRole,
    });
    if (!error) {
      setSelectedProject('');
      setAddingProject(false);
      loadMemberships();
      onRefresh();
    }
  };

  const handleRemoveProject = async (projectId: string) => {
    await supabase.from('project_members')
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', user.id);
    loadMemberships();
    onRefresh();
  };

  const roleConf = ROLE_CONFIG[user.role] || { label: user.role, color: 'bg-slate-100 text-slate-600' };
  const initials = (name: string) => name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
  const fmtDate = (d: string) => new Date(d).toLocaleDateString('pt-BR');

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Overlay */}
      <div className="flex-1 bg-black/30" onClick={onClose}></div>

      {/* Drawer */}
      <div className="w-full max-w-md bg-white h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 flex items-center justify-center rounded-full text-sm font-bold ${
                user.is_active ? 'bg-cyan-100 text-cyan-700' : 'bg-slate-100 text-slate-400'
              }`}>
                {initials(user.full_name || 'U')}
              </div>
              <div>
                <h2 className="text-slate-800 font-semibold text-base">{user.full_name}</h2>
                <p className="text-slate-400 text-xs">{user.email}</p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${roleConf.color}`}>{roleConf.label}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${user.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                    {user.is_active ? 'Ativo' : 'Inativo'}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>

          {/* Ações */}
          <div className="flex gap-2 mt-4">
            <button
              onClick={onEdit}
              className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 border border-slate-200 text-slate-600 hover:bg-slate-50 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-edit-line text-sm"></i>
              Editar
            </button>
            <button
              onClick={onToggleActive}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap border ${
                user.is_active
                  ? 'border-amber-200 text-amber-600 hover:bg-amber-50'
                  : 'border-emerald-200 text-emerald-600 hover:bg-emerald-50'
              }`}
            >
              <i className={user.is_active ? 'ri-forbid-line text-sm' : 'ri-checkbox-circle-line text-sm'}></i>
              {user.is_active ? 'Desativar' : 'Ativar'}
            </button>
          </div>
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Dados do perfil */}
          <div>
            <h3 className="text-slate-600 text-xs font-semibold uppercase tracking-wide mb-3">Dados do Perfil</h3>
            <div className="space-y-2">
              {[
                { icon: 'ri-mail-line', label: 'E-mail', value: user.email },
                { icon: 'ri-phone-line', label: 'Telefone', value: user.phone || '—' },
                { icon: 'ri-building-line', label: 'Cliente', value: user._client_name || '—' },
                { icon: 'ri-calendar-line', label: 'Cadastrado em', value: fmtDate(user.created_at) },
              ].map(item => (
                <div key={item.label} className="flex items-center gap-3 py-2 border-b border-slate-50">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-slate-50 shrink-0">
                    <i className={`${item.icon} text-slate-400 text-sm`}></i>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-400 text-xs">{item.label}</p>
                    <p className="text-slate-700 text-sm truncate">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Projetos vinculados */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-slate-600 text-xs font-semibold uppercase tracking-wide">Projetos Vinculados</h3>
              <button
                onClick={() => setAddingProject(!addingProject)}
                className="flex items-center gap-1 text-xs text-cyan-500 hover:text-cyan-600 cursor-pointer font-medium"
              >
                <i className={addingProject ? 'ri-close-line' : 'ri-add-line'}></i>
                {addingProject ? 'Cancelar' : 'Vincular'}
              </button>
            </div>

            {/* Form vincular projeto */}
            {addingProject && (
              <div className="bg-slate-50 rounded-lg p-3 mb-3 space-y-2">
                <select
                  value={selectedProject}
                  onChange={e => setSelectedProject(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer bg-white"
                >
                  <option value="">Selecionar projeto...</option>
                  {availableProjects.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                <select
                  value={selectedRole}
                  onChange={e => setSelectedRole(e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg text-slate-700 focus:outline-none focus:ring-2 focus:ring-cyan-300 cursor-pointer bg-white"
                >
                  <option value="viewer">Visualizador</option>
                  <option value="editor">Editor</option>
                  <option value="admin">Admin do Projeto</option>
                </select>
                <button
                  onClick={handleAddProject}
                  disabled={!selectedProject}
                  className="w-full py-2 bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-200 text-white rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap"
                >
                  Vincular ao Projeto
                </button>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center py-6">
                <i className="ri-loader-4-line animate-spin text-cyan-500"></i>
              </div>
            ) : memberships.length === 0 ? (
              <div className="text-center py-6 text-slate-400">
                <i className="ri-folder-line text-2xl mb-2"></i>
                <p className="text-xs">Nenhum projeto vinculado</p>
              </div>
            ) : (
              <div className="space-y-2">
                {memberships.map(m => {
                  const pst = PROJECT_STATUS_CONFIG[m._project_status || ''] || { label: m._project_status || '', color: 'bg-slate-100 text-slate-600' };
                  return (
                    <div key={m.project_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                      <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white border border-slate-200 shrink-0">
                        <i className="ri-folder-line text-slate-400 text-sm"></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-slate-700 text-sm font-medium truncate">{m._project_name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="text-slate-400 text-xs">{m._client_name}</span>
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${pst.color}`}>{pst.label}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="px-2 py-0.5 rounded text-xs bg-white border border-slate-200 text-slate-500">
                          {m.role === 'viewer' ? 'Visualizador' : m.role === 'editor' ? 'Editor' : m.role === 'admin' ? 'Admin' : m.role}
                        </span>
                        <button
                          onClick={() => handleRemoveProject(m.project_id)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer"
                          title="Desvincular"
                        >
                          <i className="ri-close-line text-sm"></i>
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}